import { z } from "zod"

/**
 * Аналитика географии потоков площадки (admin). Отвечает на вопрос собственника:
 * ГДЕ находятся продавцы (регион-источник — из ship-from origin продавца) против
 * того, КУДА уходят заказы (регион-получатель — из адреса доставки заказа).
 *
 * «Регион» здесь — город (отдельного поля региона в данных нет): и origin
 * продавца, и адрес доставки хранят город. Пустой город сворачивается в
 * «Не указан», чтобы срез не терял строки.
 */

/** Регион-источник: сколько там продавцов и какой GMV из него отгружается. */
export const GeoFlowOriginSchema = z.object({
  region: z.string(),
  sellers: z.number().int().nonnegative(),
  gmv: z.number().nonnegative(),
})
export type GeoFlowOrigin = z.infer<typeof GeoFlowOriginSchema>

/** Регион-получатель: сколько заказов туда ушло и на какой GMV. */
export const GeoFlowDestinationSchema = z.object({
  region: z.string(),
  orders: z.number().int().nonnegative(),
  gmv: z.number().nonnegative(),
})
export type GeoFlowDestination = z.infer<typeof GeoFlowDestinationSchema>

/** Ребро потока: из региона-источника в регион-получатель. */
export const GeoFlowEdgeSchema = z.object({
  fromRegion: z.string(),
  toRegion: z.string(),
  orders: z.number().int().nonnegative(),
  gmv: z.number().nonnegative(),
})
export type GeoFlowEdge = z.infer<typeof GeoFlowEdgeSchema>

/**
 * Покупатели по городам: сколько покупателей указали этот город в профиле
 * (buyer_profiles.city). «Регион» здесь — тот же город, что и у origins/
 * destinations (отдельного поля региона в данных нет). Пустой город пропускается.
 */
export const GeoFlowBuyerSchema = z.object({
  region: z.string(),
  count: z.number().int().nonnegative(),
})
export type GeoFlowBuyer = z.infer<typeof GeoFlowBuyerSchema>

/** Полный срез геопотоков — ответ GET /admin/analytics/geo-flows. */
export const GeoFlowAnalyticsSchema = z.object({
  origins: z.array(GeoFlowOriginSchema),
  destinations: z.array(GeoFlowDestinationSchema),
  flows: z.array(GeoFlowEdgeSchema),
  // Покупатели по городам профиля. Опционально с дефолтом [], чтобы старые
  // ответы/клиенты не ломались.
  buyers: z.array(GeoFlowBuyerSchema).default([]),
  // fd-analytics-04: те же получатели в разрезе субъектов РФ и федеральных
  // округов. Регион - из адреса заказа, у старых заказов - по городу через
  // справочник; не опознан - «Не указан». Суммы равны сумме по городам.
  destinationRegions: z.array(GeoFlowDestinationSchema).default([]),
  destinationDistricts: z.array(GeoFlowDestinationSchema).default([]),
})
export type GeoFlowAnalytics = z.infer<typeof GeoFlowAnalyticsSchema>
